import { useMemo } from "react";
import { Button, TreeSelect } from "antd";
import dayjs from "dayjs";
import { SelectColumn } from "react-data-grid";
import { BACKEND_DATE_FORMAT } from "../../../consts/index.js";
import TableFilter from "./TableFilter.jsx";
import { stopHeaderSort } from "./harvestEntryHelpers.js";
import { STATUS } from "./harvestEntryConstants.js";

export function useHarvestEntryColumns({
  bayRowFilter,
  setBayRowFilter,
  handleBayRowFilterChange,
  bayRowTreeData,
  bayRowExpandedKeys,
  setBayRowExpandedKeys,
  statusOptions,
  statusColFilterValue,
  setStatusColFilterValue,
  renderStatusEditCell,
  renderDateEditCell,
  renderNumberEditCell,
  getEffectiveYield,
  getEffectiveWeight,
  getEffectiveStatus,
  statusPill,
  yieldLabel,
  weightLabel,
  showWeight,
  computeWidths,
  getGridPopupContainer,
  dropdownZIndex,
  harvestConfig,
  isRowDiscarded,
  renderTubWeightEditCell,
  renderDiscardReasonEditCell,
  renderDiscardNoteEditCell,
}) {
  return useMemo(() => {
    const W = computeWidths();
    const FILTER_SIDE_PAD = 10;

    const canEditHarvestFields = (row) => !row.pendingUnharvest;

    const canEditNumbers = (row) => {
      if (row.pendingUnharvest) return false;
      const s = getEffectiveStatus(row);
      if (s === STATUS.DISCARDED) return false;
      return true;
    };

    const headerCenter =
      "harvest-rdg-header-cell harvest-rdg-header-cell--center";
    const cellCenter = "harvest-rdg-cell harvest-rdg-cell--center";

    const selectCol = {
      ...SelectColumn,
      width: 44,
      minWidth: 44,
      maxWidth: 44,
      frozen: true,
      headerCellClass:
        "harvest-select-cell harvest-rdg-header-cell harvest-rdg-header-cell--first harvest-rdg-header-cell--center",
      cellClass:
        "harvest-select-cell harvest-rdg-cell harvest-rdg-header-cell--first harvest-rdg-cell--center",
      renderHeaderCell: (props) => {
        const Base = SelectColumn.renderHeaderCell;
        return (
          <div
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
              paddingBottom: 10,
            }}
          >
            <div style={{ display: "flex", justifyContent: "center" }}>
              {typeof Base === "function" ? <Base {...props} /> : null}
            </div>
          </div>
        );
      },
    };

    const bayRowHeader = (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            alignItems: "center",
            fontWeight: 800,
            fontSize: 14,
            lineHeight: "20px",
            textAlign: "center",
          }}
        >
          <div style={{ borderRight: "1px solid rgba(0,0,0,0.12)" }}>Bay</div>
          <div>Row</div>
        </div>

        <div
          style={{ padding: `0 ${FILTER_SIDE_PAD}px` }}
          onMouseDown={stopHeaderSort}
          onPointerDown={stopHeaderSort}
          onTouchStart={stopHeaderSort}
          onClick={stopHeaderSort}
          onDoubleClick={stopHeaderSort}
        >
          <TreeSelect
            value={bayRowFilter}
            onChange={handleBayRowFilterChange}
            treeData={bayRowTreeData}
            treeCheckable
            allowClear
            showCheckedStrategy={TreeSelect.SHOW_PARENT}
            placeholder="Filter..."
            style={{ width: "100%", fontSize: 14 }}
            styles={{
              popup: {
                root: {
                  maxHeight: 520,
                  overflow: "auto",
                  zIndex: dropdownZIndex,
                  fontSize: 14,
                  lineHeight: "24px",
                },
              },
            }}
            popupMatchSelectWidth={false}
            getPopupContainer={getGridPopupContainer}
            treeExpandedKeys={bayRowExpandedKeys}
            onTreeExpand={setBayRowExpandedKeys}
            maxTagCount="responsive"
            listHeight={520}
            treeTitleRender={(node) => (
              <span style={{ fontSize: 14, lineHeight: "26px" }}>
                {node.title}
              </span>
            )}
            popupRender={(menu) => (
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 10px",
                    borderBottom: "1px solid #eee",
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: 14,
                      lineHeight: "20px",
                    }}
                  >
                    Bay / Row Filter
                  </div>

                  <Button size="small" onClick={() => setBayRowFilter([])}>
                    Clear
                  </Button>
                </div>
                {menu}
              </div>
            )}
          />
        </div>
      </div>
    );

    const cols = [
      selectCol,
      {
        key: "bay_row",
        name: "Bay / Row",
        width: W.bayRow,
        frozen: true,
        sortable: true,
        headerCellClass: headerCenter,
        cellClass: cellCenter,
        renderHeaderCell: () => bayRowHeader,
        renderCell: ({ row }) => (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              alignItems: "center",
              height: "100%",
              textAlign: "center",
            }}
          >
            <div
              style={{
                padding: "0 6px",
                fontWeight: 800,
                fontSize: 16,
                lineHeight: "20px",
              }}
              title={row.bay_name}
            >
              {row.bay_name}
            </div>

            <div
              style={{ padding: "0 6px", fontSize: 16, lineHeight: "20px" }}
              title={row.row_name}
            >
              {row.row_name}
            </div>
          </div>
        ),
      },
      {
        key: "status",
        name: "Status",
        width: W.status,
        sortable: true,
        editable: true,
        editorOptions: { editOnClick: true },
        headerCellClass: headerCenter,
        cellClass: cellCenter,
        renderHeaderCell: (p) => (
          <div style={{ padding: `0 ${FILTER_SIDE_PAD}px` }}>
            <TableFilter
              {...p}
              label="Status"
              kind="select"
              options={statusOptions}
              value={statusColFilterValue}
              onChange={setStatusColFilterValue}
              popupMatchSelectWidth={false}
              getPopupContainer={getGridPopupContainer}
              popupZIndex={dropdownZIndex}
            />
          </div>
        ),
        renderEditCell: renderStatusEditCell,
        renderCell: ({ row }) => statusPill(row),
      },
      {
        key: "harvest_date",
        name: "Harvest / Discard Date",
        width: W.date,
        editable: (row) => canEditHarvestFields(row),
        editorOptions: { editOnClick: true },
        headerCellClass: headerCenter,
        cellClass: cellCenter,
        renderEditCell: renderDateEditCell,
        renderCell: ({ row }) => {
          const raw = row.pendingHarvestDate || row.harvest_date || "";
          const value = raw ? dayjs(raw, BACKEND_DATE_FORMAT) : null;

          return (
            <div style={{ lineHeight: "18px", textAlign: "center" }}>
              <div style={{ fontWeight: 600 }}>
                {value ? value.format("DD MMM, YYYY") : <span>&nbsp;</span>}
              </div>
            </div>
          );
        },
      },
      {
        key: "pendingYield",
        name: yieldLabel,
        width: W.yield,
        editable: (row) => canEditNumbers(row),
        editorOptions: { editOnClick: true },
        headerCellClass: headerCenter,
        cellClass: cellCenter,
        renderEditCell: renderNumberEditCell,
        renderCell: ({ row }) => {
          const val = getEffectiveYield(row);
          return (
            <div style={{ textAlign: "center", fontWeight: 600 }}>
              {isRowDiscarded(row) ? "N/A" : val == null ? "" : String(val)}
            </div>
          );
        },
      },
    ];

    if (showWeight) {
      cols.push({
        key: "pendingWeight",
        name: weightLabel,
        width: W.weight,
        headerCellClass: headerCenter,
        cellClass: cellCenter,
        renderCell: ({ row }) => {
          if (isRowDiscarded(row)) {
            return <div style={{ textAlign: "center", fontWeight: 600 }}>N/A</div>;
          }
          // Mirror save logic: derive total weight from yield * tubWeight when
          // either is pending, so the display always matches what will be saved.
          const hasPendingYield =
            row.pendingYield !== null && row.pendingYield !== undefined;
          const hasPendingTubWeight =
            row.pendingTubWeight !== null && row.pendingTubWeight !== undefined;
          let val;
          if (hasPendingYield || hasPendingTubWeight) {
            const effectiveYield = hasPendingYield ? row.pendingYield : (row.yield ?? 0);
            const defaultTubWeight = harvestConfig?.tub_weight?.default ?? 0;
            const effectiveTubWeight = hasPendingTubWeight
              ? row.pendingTubWeight
              : (row.weight_per_tub ?? defaultTubWeight);
            val = effectiveYield * effectiveTubWeight;
          } else {
            val = getEffectiveWeight(row);
          }
          return (
            <div style={{ textAlign: "center", fontWeight: 600 }}>
              {val == null ? "" : String(val)}
            </div>
          );
        },
      });
    }

    cols.push({
      key: "pendingTubWeight",
      name: "Tub Weight (kg)",
      width: W.tubWeight,
      minWidth: 95,
      editable: (row) => !isRowDiscarded(row) && !row.pendingUnharvest,
      editorOptions: { editOnClick: true },
      headerCellClass: headerCenter,
      cellClass: cellCenter,
      renderEditCell: renderTubWeightEditCell,
      renderCell: ({ row }) => {
        const defaultTubWeight = harvestConfig?.tub_weight?.default;
        const val =
          row.pendingTubWeight !== null && row.pendingTubWeight !== undefined
            ? row.pendingTubWeight
            : (row.weight_per_tub ?? defaultTubWeight);
        const isDefault =
          (row.pendingTubWeight === null || row.pendingTubWeight === undefined) &&
          (row.weight_per_tub_source ?? "SITE_DEFAULT") === "SITE_DEFAULT";
        const isReadonly = isRowDiscarded(row) || !!row.pendingUnharvest;
        const isOutOfRange =
          !isReadonly &&
          val != null &&
          harvestConfig?.tub_weight?.min != null &&
          (val < harvestConfig.tub_weight.min || val > harvestConfig.tub_weight.max);
        return (
          <div
            style={{
              textAlign: "center",
              fontWeight: isDefault ? undefined : 600,
              color: isOutOfRange ? "#ff4d4f" : isDefault ? "#999" : undefined,
              padding: "0 8px",
            }}
          >
            {val == null ? "" : String(val)}
          </div>
        );
      },
    });

    cols.push({
      key: "pendingPloughedReasons",
      name: "Discard Reason",
      width: W.ploughedReasons,
      editable: (row) =>
        !row.pendingUnharvest &&
        (isRowDiscarded(row) || getEffectiveStatus(row) === STATUS.PLANTED),
      editorOptions: { editOnClick: true },
      headerCellClass: headerCenter,
      cellClass: cellCenter,
      renderEditCell: renderDiscardReasonEditCell,
      renderCell: ({ row }) => {
        if (row.pendingUnharvest) return null;
        if (!isRowDiscarded(row) && getEffectiveStatus(row) !== STATUS.PLANTED)
          return null;
        const backendIds = (row.ploughed_under_reasons ?? []).map((r) => r.id);
        const currentIds =
          row.pendingPloughedReasons !== null &&
          row.pendingPloughedReasons !== undefined
            ? row.pendingPloughedReasons
            : backendIds;
        const options = [...(harvestConfig?.discard_reasons ?? [])]
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((r) => ({ value: r.id, label: r.label }));
        const selectedOption = options.find((o) => o.value === currentIds[0]);
        return (
          <div style={{ padding: "0 8px", textAlign: "center" }}>
            {selectedOption ? (
              selectedOption.label
            ) : (
              <span style={{ color: "#999" }}>Select reason…</span>
            )}
          </div>
        );
      },
    });

    cols.push({
      key: "pendingPloughedNote",
      name: "Discard Note",
      width: W.ploughedNote,
      editable: (row) =>
        !row.pendingUnharvest &&
        (isRowDiscarded(row) || getEffectiveStatus(row) === STATUS.PLANTED),
      editorOptions: { editOnClick: true },
      headerCellClass: headerCenter,
      cellClass: cellCenter,
      renderEditCell: renderDiscardNoteEditCell,
      renderCell: ({ row }) => {
        if (row.pendingUnharvest) return null;
        if (!isRowDiscarded(row) && getEffectiveStatus(row) !== STATUS.PLANTED)
          return null;
        const note = row.pendingPloughedNote ?? row.ploughed_under_note ?? "";
        return (
          <div style={{ padding: "0 8px" }}>
            {note || <span style={{ color: "#999" }}>Add note…</span>}
          </div>
        );
      },
    });

    return cols;
  }, [
    bayRowFilter,
    bayRowTreeData,
    bayRowExpandedKeys,
    computeWidths,
    dropdownZIndex,
    getEffectiveStatus,
    getEffectiveWeight,
    getEffectiveYield,
    getGridPopupContainer,
    handleBayRowFilterChange,
    harvestConfig,
    isRowDiscarded,
    renderDateEditCell,
    renderDiscardNoteEditCell,
    renderDiscardReasonEditCell,
    renderNumberEditCell,
    renderStatusEditCell,
    renderTubWeightEditCell,
    setBayRowExpandedKeys,
    setBayRowFilter,
    setStatusColFilterValue,
    showWeight,
    statusColFilterValue,
    statusOptions,
    statusPill,
    weightLabel,
    yieldLabel,
  ]);
}
